import { useStores } from '@littlebird/sdk';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { queryClient } from '@/query/client';

// Delayed refetches so the list/widget pick up freshly synced events without
// waiting on the 60s poll.
const REFETCH_DELAYS_MS = [5_000, 15_000];

function refetchCalendarEvents() {
  queryClient.invalidateQueries({ queryKey: ['calendar', 'events'] });
}

export function useManualCalendarSync() {
  const { meetings } = useStores();
  const [isSyncing, setIsSyncing] = useState(false);
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearRefetchTimers = useCallback(() => {
    for (const timer of timersRef.current) clearTimeout(timer);
    timersRef.current = [];
  }, []);

  useEffect(() => () => clearRefetchTimers(), [clearRefetchTimers]);

  const scheduleRefetchTimers = useCallback(() => {
    clearRefetchTimers();
    for (const delay of REFETCH_DELAYS_MS) {
      const timer = setTimeout(() => {
        refetchCalendarEvents();
        timersRef.current = timersRef.current.filter((t) => t !== timer);
      }, delay);
      timersRef.current.push(timer);
    }
  }, [clearRefetchTimers]);

  const syncNow = useCallback(async () => {
    setIsSyncing(true);
    try {
      const result = await meetings.syncCalendarNow();

      const reconnectNeeded = result.accounts.some(
        (account) =>
          account.connection_status === 'expired' ||
          account.connection_status === 'failed'
      );
      setNeedsReconnect(reconnectNeeded);

      const hasConnected = result.accounts.some(
        (account) => account.connection_status === 'connected'
      );
      if (result.sync_started) {
        toast.info('Syncing started. Events will show up shortly.');
        refetchCalendarEvents();
        scheduleRefetchTimers();
      } else if (hasConnected) {
        // Probe saw a connected account but no sync started — the workflow
        // failed to start. Accounts are healthy, so this isn't a reconnect case.
        toast.error('Could not start calendar sync. Please try again.');
      } else if (reconnectNeeded) {
        toast.error('Reconnect your calendar in Settings → Integrations.');
      } else {
        toast.error('Connect a calendar in Settings → Integrations.');
      }
    } catch {
      toast.error('Could not refresh your calendar. Please try again.');
    } finally {
      setIsSyncing(false);
    }
  }, [meetings, scheduleRefetchTimers]);

  return { isSyncing, syncNow, needsReconnect };
}
